import { useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import ProductDetailsLabel from "./ProductDetailsLabel";
import ProductImagesLightBox from "./ProductImagesLightBox";
import { Search as SearchIcon } from "@/assets/icons/24/Search";
import { BunnyPicture } from "@/common/components/bunny-picture";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import { buildProductImageAlt } from "@/common/utils/product-image.utils";
import { Button } from "@/ui/cva/Button";

const NUMBER_OF_IMAGES_TO_LOAD = 4;

type ProductDetailsGalleryProps = {
  className?: string;
  product: ProductBySlugNonNullable;
};

const ProductDetailsGallery = ({
  product,
  className,
}: ProductDetailsGalleryProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const [imagesPerLoad, setImagesPerLoad] = useState(NUMBER_OF_IMAGES_TO_LOAD);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const images = product.images;
  const remainingImagesCount = images.length - imagesPerLoad;

  const handleLoadMoreClick = () => {
    setImagesPerLoad(prevState => {
      return prevState + NUMBER_OF_IMAGES_TO_LOAD;
    });
  };

  const handleImageClick = (index: number) => {
    setOpenLightBox(true);
    setImageIndex(index);

    /** Trigger `image_zoom_pdp` event. */
    analyticsService.trackImageZoomPdp(index + 1);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
  };

  return (
    <div className={className}>
      {images.map((image, index) => (
        <div
          key={image.id}
          className={cn(
            "aspect-square",
            index + 1 > imagesPerLoad ? "hidden" : "relative"
          )}
        >
          <div className="">
            <BunnyPicture
              preload={index === 0}
              draggable={false}
              className="absolute bottom-0 left-0 right-0 top-0"
              imgClassName="h-full w-full object-cover"
              src={image.url}
              width={image.width ?? 0}
              height={image.height ?? 0}
              sizes={[{ media: "(min-width: 769px)", width: "560px" }]}
              alt={buildProductImageAlt(product, index, images.length, locale)}
            />
            <button
              onClick={() => handleImageClick(index)}
              className="absolute top-0 flex h-full w-full items-center justify-center bg-[#282F36] bg-opacity-50 text-white opacity-0 transition duration-300 ease-in-out hover:opacity-100"
              aria-label={t("pdp:pdp-show-detailed-image", {
                index: index + 1,
              })}
            >
              <SearchIcon className="h-12 w-12" />
            </button>
            {index === 0 && (
              <ProductDetailsLabel
                product={product}
                className="absolute left-2 top-2"
              />
            )}
          </div>
        </div>
      ))}
      <ProductImagesLightBox
        productId={product.id}
        imageIndex={imageIndex}
        images={images}
        isOpen={openLightBox}
        onClose={handleCloseLightBox}
      />
      {remainingImagesCount > 0 && (
        <Button
          appearance="outlined"
          className="col-span-2 w-max justify-self-center"
          onClick={handleLoadMoreClick}
          size="large"
        >
          {t("pdp:pdp-gallery-load-more", { count: remainingImagesCount })}
        </Button>
      )}
    </div>
  );
};

export default ProductDetailsGallery;
